import React, { useState, useRef, useCallback, useEffect } from 'react';

interface ScreenshotCaptureProps {
  /** Container element to capture from */
  containerRef?: React.RefObject<HTMLElement | null>;
  /** Callback when screenshot is captured. Receives dataUrl and screenshotId */
  onScreenshotCaptured?: (dataUrl: string, screenshotId: string) => void;
  /** Position of the capture button */
  buttonPosition?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  /** Custom className for the button */
  buttonClassName?: string;
  /** Cache expiration time in milliseconds (default: 1 hour) */
  cacheExpirationMs?: number;
}

interface CachedScreenshot {
  dataUrl: string;
  timestamp: number;
  id: string;
}

type Point = { x: number; y: number };

const SCREENSHOT_CACHE_PREFIX = 'vishmaker_screenshot_';
const DEFAULT_CACHE_EXPIRATION_MS = 60 * 60 * 1000; // 1 hour
const DRAG_THRESHOLD_PX = 6;

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

/**
 * ScreenshotCapture component that allows selecting an area on screen and capturing it.
 * Screenshots are stored in localStorage with automatic expiration.
 */
const ScreenshotCapture: React.FC<ScreenshotCaptureProps> = ({
  containerRef,
  onScreenshotCaptured,
  buttonPosition = 'bottom-left',
  buttonClassName = '',
  cacheExpirationMs = DEFAULT_CACHE_EXPIRATION_MS
}) => {
  const [isSelecting, setIsSelecting] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [selectionRect, setSelectionRect] = useState<DOMRect | null>(null);
  const [hoveredElement, setHoveredElement] = useState<{ element: Element; rect: DOMRect } | null>(null);
  const [captureButtonPos, setCaptureButtonPos] = useState<{ x: number; y: number } | null>(null);
  
  const containerElementRef = useRef<HTMLElement | null>(null);
  const startRef = useRef<{ point: Point; time: number } | null>(null);
  const lastPointRef = useRef<Point | null>(null);
  const draggingRef = useRef(false);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const highlightRef = useRef<HTMLDivElement | null>(null);

  // Get container element
  useEffect(() => {
    if (containerRef?.current) {
      containerElementRef.current = containerRef.current;
    } else {
      containerElementRef.current = document.body;
    }
  }, [containerRef]);

  // Cleanup expired screenshots on mount
  useEffect(() => {
    cleanupExpiredScreenshots();
    // Set up periodic cleanup every 5 minutes
    const interval = setInterval(cleanupExpiredScreenshots, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [cacheExpirationMs]);

  /**
   * Clean up expired screenshots from localStorage
   */
  const cleanupExpiredScreenshots = useCallback(() => {
    try {
      const now = Date.now();
      const keysToDelete: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(SCREENSHOT_CACHE_PREFIX)) {
          try {
            const cached = JSON.parse(localStorage.getItem(key) || '{}') as CachedScreenshot;
            if (cached.timestamp && (now - cached.timestamp) > cacheExpirationMs) {
              keysToDelete.push(key);
            }
          } catch (e) {
            // Invalid cache entry, delete it
            keysToDelete.push(key);
          }
        }
      }
      
      keysToDelete.forEach(key => localStorage.removeItem(key));
      if (keysToDelete.length > 0) {
        console.log(`Cleaned up ${keysToDelete.length} expired screenshot(s)`);
      }
    } catch (e) {
      console.error('Error cleaning up expired screenshots:', e);
    }
  }, [cacheExpirationMs]);

  /**
   * Store screenshot in cache
   */
  const storeScreenshot = useCallback((dataUrl: string): string => {
    const screenshotId = `${SCREENSHOT_CACHE_PREFIX}${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const cached: CachedScreenshot = {
      dataUrl,
      timestamp: Date.now(),
      id: screenshotId
    };
    
    try {
      localStorage.setItem(screenshotId, JSON.stringify(cached));
      return screenshotId;
    } catch (e) {
      // If localStorage is full, try to clean up and retry
      console.warn('localStorage full, attempting cleanup...');
      cleanupExpiredScreenshots();
      try {
        localStorage.setItem(screenshotId, JSON.stringify(cached));
        return screenshotId;
      } catch (e2) {
        console.error('Failed to store screenshot:', e2);
        throw e2;
      }
    }
  }, [cleanupExpiredScreenshots]);

  /**
   * Load html2canvas library
   */
  const loadHtml2Canvas = useCallback((): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (typeof (window as any).html2canvas !== 'undefined') {
        resolve((window as any).html2canvas);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
      script.onload = () => resolve((window as any).html2canvas);
      script.onerror = () => reject(new Error('Failed to load html2canvas'));
      document.head.appendChild(script);
    });
  }, []);

  /**
   * Start selection mode
   */
  const startSelection = useCallback(() => {
    setIsSelecting(true);
    setSelectionRect(null);
    setHoveredElement(null);
    setCaptureButtonPos(null);
    startRef.current = null;
    lastPointRef.current = null;
    draggingRef.current = false;
  }, []);

  /**
   * Cancel selection mode
   */
  const cancelSelection = useCallback(() => {
    setIsSelecting(false);
    setSelectionRect(null);
    setHoveredElement(null);
    setCaptureButtonPos(null);
    startRef.current = null;
    lastPointRef.current = null;
    draggingRef.current = false;
  }, []);

  /**
   * Handle mouse move to highlight elements
   */
  const handleHoverMove = useCallback((e: MouseEvent) => {
    if (!isSelecting || draggingRef.current || startRef.current) {
      setHoveredElement(null);
      return;
    }
    
    const container = containerElementRef.current;
    if (!container) return;
    
    // Check if element is within container
    const containerRect = container.getBoundingClientRect();
    if (
      e.clientX < containerRect.left ||
      e.clientX > containerRect.right ||
      e.clientY < containerRect.top ||
      e.clientY > containerRect.bottom
    ) {
      setHoveredElement(null);
      return;
    }
    
    // Get element under cursor (excluding overlay elements)
    const element = document.elementFromPoint(e.clientX, e.clientY);
    if (!element) {
      setHoveredElement(null);
      return;
    }
    
    // Skip if clicking on overlay or its children
    if (
      overlayRef.current?.contains(element) ||
      highlightRef.current?.contains(element)
    ) {
      return;
    }
    
    // Skip if element is the container itself
    if (element === container) {
      setHoveredElement(null);
      return;
    }
    
    // Get element's bounding rect
    const rect = element.getBoundingClientRect();
    
    // Only highlight if element has meaningful size
    if (rect.width > 5 && rect.height > 5) {
      setHoveredElement({ element, rect });
    } else {
      setHoveredElement(null);
    }
  }, [isSelecting]);

  /**
   * Capture screenshot of selected area
   */
  const captureScreenshot = useCallback(async () => {
    if (!selectionRect || !containerElementRef.current) return;
    
    setIsCapturing(true);
    
    try {
      // Load html2canvas if needed
      const html2canvas = await loadHtml2Canvas();
      
      const container = containerElementRef.current;
      const containerRect = container.getBoundingClientRect();
      
      // Convert viewport coordinates to container-relative coordinates
      const left = selectionRect.left - containerRect.left + (container.scrollLeft || 0);
      const top = selectionRect.top - containerRect.top + (container.scrollTop || 0);
      const width = selectionRect.width;
      const height = selectionRect.height;
      
      // Capture the entire container
      const canvas = await html2canvas(container, {
        useCORS: true,
        logging: false,
        scale: 1,
        allowTaint: false,
        backgroundColor: null,
        width: container.scrollWidth,
        height: container.scrollHeight
      });
      
      // Create a new canvas for the cropped area
      const croppedCanvas = document.createElement('canvas');
      croppedCanvas.width = width;
      croppedCanvas.height = height;
      const ctx = croppedCanvas.getContext('2d');
      
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }
      
      // Draw the cropped portion
      ctx.drawImage(
        canvas,
        left, top, width, height,
        0, 0, width, height
      );
      
      // Convert to data URL
      const dataUrl = croppedCanvas.toDataURL('image/png');
      
      // Store in cache
      const screenshotId = storeScreenshot(dataUrl);
      
      // Call callback
      if (onScreenshotCaptured) {
        onScreenshotCaptured(dataUrl, screenshotId);
      }
      
      // Reset selection
      cancelSelection();
      
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      alert('Failed to capture screenshot. Please try again.');
    } finally {
      setIsCapturing(false);
    }
  }, [selectionRect, loadHtml2Canvas, storeScreenshot, onScreenshotCaptured, cancelSelection]);

  // Set up pointer event listeners for selection
  useEffect(() => {
    if (!isSelecting) return;
    
    const container = containerElementRef.current;
    if (!container) return;
    
    const doc = container.ownerDocument;
    
    const onPointerDown = (e: PointerEvent) => {
      // Only primary button
      if (e.button !== 0) return;
      
      // Don't start if clicking on overlay elements
      const target = e.target as Element;
      if (overlayRef.current?.contains(target) || highlightRef.current?.contains(target)) {
        return;
      }
      
      startRef.current = { point: { x: e.clientX, y: e.clientY }, time: Date.now() };
      lastPointRef.current = { x: e.clientX, y: e.clientY };
      draggingRef.current = false;
      setSelectionRect(null);
      setCaptureButtonPos(null);
    };
    
    const onPointerMove = (e: PointerEvent) => {
      if (!startRef.current) return;
      lastPointRef.current = { x: e.clientX, y: e.clientY };
      
      const dx = e.clientX - startRef.current.point.x;
      const dy = e.clientY - startRef.current.point.y;
      if (!draggingRef.current && Math.hypot(dx, dy) >= DRAG_THRESHOLD_PX) {
        draggingRef.current = true;
      }
      
      // Update selection rectangle while dragging
      if (draggingRef.current && lastPointRef.current) {
        const a = startRef.current.point;
        const b = lastPointRef.current;
        
        const left = Math.min(a.x, b.x);
        const top = Math.min(a.y, b.y);
        const right = Math.max(a.x, b.x);
        const bottom = Math.max(a.y, b.y);
        
        const dragRect = new DOMRect(left, top, right - left, bottom - top);
        setSelectionRect(dragRect);
        
        // Position capture button
        const x = clamp(dragRect.right + 8, 8, window.innerWidth - 48);
        const y = clamp(dragRect.top - 8, 8, window.innerHeight - 48);
        setCaptureButtonPos({ x, y });
      }
    };
    
    const onPointerUp = () => {
      const start = startRef.current;
      if (!start) return;
      
      // If hovering over an element and didn't drag much, use that element
      if (hoveredElement && !draggingRef.current && lastPointRef.current) {
        const dx = Math.abs(lastPointRef.current.x - start.point.x);
        const dy = Math.abs(lastPointRef.current.y - start.point.y);
        
        if (dx < DRAG_THRESHOLD_PX && dy < DRAG_THRESHOLD_PX) {
          setSelectionRect(hoveredElement.rect);
          const x = clamp(hoveredElement.rect.right + 8, 8, window.innerWidth - 48);
          const y = clamp(hoveredElement.rect.top - 8, 8, window.innerHeight - 48);
          setCaptureButtonPos({ x, y });
        } else if (draggingRef.current && lastPointRef.current) {
          // Use drag selection
          const a = start.point;
          const b = lastPointRef.current;
          
          const left = Math.min(a.x, b.x);
          const top = Math.min(a.y, b.y);
          const right = Math.max(a.x, b.x);
          const bottom = Math.max(a.y, b.y);
          
          const dragRect = new DOMRect(left, top, right - left, bottom - top);
          setSelectionRect(dragRect);
          
          const x = clamp(dragRect.right + 8, 8, window.innerWidth - 48);
          const y = clamp(dragRect.top - 8, 8, window.innerHeight - 48);
          setCaptureButtonPos({ x, y });
        }
      } else if (draggingRef.current && lastPointRef.current) {
        // Use drag selection
        const a = start.point;
        const b = lastPointRef.current;
        
        const left = Math.min(a.x, b.x);
        const top = Math.min(a.y, b.y);
        const right = Math.max(a.x, b.x);
        const bottom = Math.max(a.y, b.y);
        
        const dragRect = new DOMRect(left, top, right - left, bottom - top);
        setSelectionRect(dragRect);
        
        const x = clamp(dragRect.right + 8, 8, window.innerWidth - 48);
        const y = clamp(dragRect.top - 8, 8, window.innerHeight - 48);
        setCaptureButtonPos({ x, y });
      }
      
      startRef.current = null;
      lastPointRef.current = null;
      draggingRef.current = false;
    };
    
    container.addEventListener('pointerdown', onPointerDown, { passive: true });
    container.addEventListener('pointermove', onPointerMove, { passive: true });
    container.addEventListener('pointerup', onPointerUp, { passive: true });
    
    // Set up hover detection
    if (!draggingRef.current && !startRef.current) {
      window.addEventListener('mousemove', handleHoverMove as EventListener, true);
    }
    
    return () => {
      container.removeEventListener('pointerdown', onPointerDown as any);
      container.removeEventListener('pointermove', onPointerMove as any);
      container.removeEventListener('pointerup', onPointerUp as any);
      window.removeEventListener('mousemove', handleHoverMove as EventListener, true);
    };
  }, [isSelecting, hoveredElement, handleHoverMove]);

  // Handle escape key to cancel selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isSelecting) {
        cancelSelection();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSelecting, cancelSelection]);

  // Calculate hover highlight rectangle
  const hoverRect = hoveredElement && !draggingRef.current && !startRef.current ? (() => {
    return {
      left: hoveredElement.rect.left,
      top: hoveredElement.rect.top,
      width: hoveredElement.rect.width,
      height: hoveredElement.rect.height
    };
  })() : null;

  // Get button position styles
  const getButtonPositionStyles = () => {
    const baseStyles: React.CSSProperties = {
      position: 'absolute',
      zIndex: 10000
    };
    
    switch (buttonPosition) {
      case 'top-left':
        return { ...baseStyles, top: 12, left: 12 };
      case 'bottom-left':
        return { ...baseStyles, bottom: 12, left: 12 };
      case 'bottom-right':
        return { ...baseStyles, bottom: 12, right: 12 };
      case 'top-right':
      default:
        return { ...baseStyles, top: 12, right: 12 };
    }
  };

  return (
    <>
      {/* Floating capture button */}
      <button
        onClick={isSelecting ? cancelSelection : startSelection}
        style={getButtonPositionStyles()}
        className={`bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2.5 shadow-lg focus:outline-none transition-all ${
          isSelecting ? 'ring-4 ring-blue-300' : ''
        } ${buttonClassName}`}
        title={isSelecting ? 'Cancel screenshot selection (ESC)' : 'Capture screenshot'}
        disabled={isCapturing}
      >
        {isCapturing ? (
          <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        ) : isSelecting ? (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        )}
      </button>

      {/* Selection overlay */}
      {isSelecting && (
        <div
          ref={overlayRef}
          className="fixed inset-0 z-[9999] cursor-crosshair"
          style={{ pointerEvents: isCapturing ? 'none' : 'auto' }}
        >
          {/* Dimmed background */}
          <div className="absolute inset-0 bg-black/20" />
          
          {/* Hover highlight (when not dragging and no selection) */}
          {hoverRect && !draggingRef.current && !startRef.current && (
            <div
              ref={highlightRef}
              className="absolute border-2 border-blue-400 bg-blue-400/20 pointer-events-none transition-all duration-75"
              style={{
                left: `${hoverRect.left}px`,
                top: `${hoverRect.top}px`,
                width: `${hoverRect.width}px`,
                height: `${hoverRect.height}px`,
              }}
            >
              {/* Hover info */}
              <div className="absolute -top-8 left-0 bg-blue-500 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                {Math.round(hoverRect.width)} × {Math.round(hoverRect.height)} px
                <span className="ml-2 opacity-70">(Click to select)</span>
              </div>
            </div>
          )}
          
          {/* Selection rectangle */}
          {selectionRect && (
            <div
              className="absolute border-2 border-blue-500 bg-blue-500/10 pointer-events-none"
              style={{
                left: `${selectionRect.left}px`,
                top: `${selectionRect.top}px`,
                width: `${selectionRect.width}px`,
                height: `${selectionRect.height}px`,
              }}
            >
              {/* Selection info */}
              <div className="absolute -top-8 left-0 bg-blue-600 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                {Math.round(selectionRect.width)} × {Math.round(selectionRect.height)} px
              </div>
            </div>
          )}
          
          {/* Capture button - positioned near selection */}
          {selectionRect && selectionRect.width >= 10 && selectionRect.height >= 10 && captureButtonPos && (
            <button
              onClick={captureScreenshot}
              disabled={isCapturing}
              style={{
                position: 'fixed',
                left: `${captureButtonPos.x}px`,
                top: `${captureButtonPos.y}px`,
                zIndex: 10001
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2.5 shadow-lg flex items-center gap-2 disabled:opacity-50 transition-all"
              title="Capture screenshot"
            >
              {isCapturing ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="text-sm">Capturing...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-sm">Capture</span>
                </>
              )}
            </button>
          )}
          
          {/* Instructions */}
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black/80 text-white text-sm px-4 py-2 rounded-lg">
            {selectionRect 
              ? 'Selection ready - click Capture button' 
              : hoveredElement 
                ? 'Move mouse to highlight elements, click to select, or drag to select custom area'
                : 'Move mouse to highlight elements, click to select, or drag to select custom area'}
            <span className="ml-2 text-xs opacity-70">(Press ESC to cancel)</span>
          </div>
        </div>
      )}
    </>
  );
};

// Export utility functions for retrieving screenshots
export const getScreenshotFromCache = (screenshotId: string): string | null => {
  try {
    const cached = JSON.parse(localStorage.getItem(screenshotId) || '{}') as CachedScreenshot;
    if (cached.dataUrl && cached.timestamp) {
      const age = Date.now() - cached.timestamp;
      const expiration = DEFAULT_CACHE_EXPIRATION_MS;
      if (age < expiration) {
        return cached.dataUrl;
      } else {
        localStorage.removeItem(screenshotId);
      }
    }
  } catch (e) {
    console.error('Error retrieving screenshot:', e);
  }
  return null;
};

export const deleteScreenshotFromCache = (screenshotId: string): void => {
  try {
    localStorage.removeItem(screenshotId);
  } catch (e) {
    console.error('Error deleting screenshot:', e);
  }
};

export default ScreenshotCapture;
